﻿import {
    ApplicationSigningType,
    CheckoutStep,
    Country,
    DataAttributes,
    GtmEventType,
    LeaseProvider,
    ShippingMethod,
    ShoppingCartAction, Site,
    TransportRestriction
} from "@/enums"
import {
    AddToCartResponse,
    EuronicsResponse,
    InCreditApiDto,
    IsTransportRestrictionResponse,
    SetPreferredPaymentMethodResp,
    UpdateCartResponse
} from "@/interfaces"
import {
    $,
    $$,
    get,
    getData,
    GetDataJsonSync,
    gtmEvent,
    hide,
    isHidden,
    isVisible,
    postData,
    postDataJson,
    postDataText,
    postFormCustom,
    clickOnEnterKey,
    postFormData,
    preserveUrlParameters,
    show,
    toggle,
    Key,
    sleep,
    waitForElement,
    closestTabbable
} from "@/Utils"

import {initCarousels} from "@/sharedComponents/Carousel/Carousel"
import {initBigBankLeaseCalculator, initRentalCalculator} from "@/sharedComponents/LeaseCalculator"
import {initLogin} from "@/sharedComponents/Header/Login"
import {closeModal, initModals, openModalById} from "@/sharedComponents/Modal"
import {bindCartPanelDeletes, bindPanelClose, openPanel} from "@/sharedComponents/Panel"
import {StatusCodes} from "http-status-codes"
import {initDatePickers, initGtmEvents, initPhoneInputs, startLoadingAnimation} from "@/Startup"
import {initToggleButtons, initPasswordChecks} from "@/RegistrationModule"
import {initInputListeners, validateElement, validateForm, validateRadioList} from "@/Validation"
import {highlightProductCard} from "@/ProductModule";
import {initStoreAndPostPackageSelections} from "@/StoreAndPostPackageSelection";
import {initAddressSearch} from "@/AddressSearchCommon";
import {initManualAddressSearch} from "@/ManualAddressSearch"
import {EuronicsResponseHandler, EuronicsResponsePromiseHandler} from "@/EuronicsResponseHandler";
import {KlaviyoEvents} from "../ts-enums/KlaviyoEvents";

export function updateHeaderCartHtml() {
    const cartPanel = get("cart-panel");
    const closeButton = cartPanel.querySelector(".panel__close") as HTMLElement;
    bindPanelClose(closeButton);
    bindCartPanelDeletes();

    const cartIconMenu = $(".cart-icon-menu");
    cartIconMenu.classList.add("icon-menu--active");

    const count = cartPanel.getAttribute("data-count");

    const countElement = cartIconMenu.querySelector(".count") as HTMLElement;

    countElement.innerText = count;

    // Duplicate for responsive
    const cartResponsive = $(".cart-r");
    cartResponsive.classList.toggle("cart-r--active", parseInt(count) > 0);

    const rCountElement = cartResponsive.querySelector(".count") as HTMLElement;
    if (rCountElement) {
        rCountElement.innerText = count;
    }
    countElement.classList.toggle("count--active", parseInt(count) > 0);
    const headerCartIcon = document.querySelector(".cart-icon use")

    if (window.location.hostname.includes("elesen.lt") && headerCartIcon) {
        const currentHref = headerCartIcon.getAttribute("href");
        if (count > 0 && currentHref.indexOf("--active") === -1){
            headerCartIcon.setAttribute("href", currentHref + "--active");
        } else {
            headerCartIcon.setAttribute("href", currentHref.replace("--active", ""));
        }
    }
}

/**
 * Vormistamise sammud. Päis ja ostukorvi eelvaade puuduvad.
 */
function updateShoppingCartHtml(data: string, fromHistory = false) {
    //until EuronicsResponse is returned everywhere, try-catch is necessary
    try {
        const euronicsResponseData = JSON.parse(data) as EuronicsResponse;
        EuronicsResponseHandler(euronicsResponseData, (r) => initGtmEvents(r), get("shoppingCart"));
        initCart(false, fromHistory);
        return;
    } catch (e) {
    }

    get("shoppingCart").innerHTML = data;
    initCart(false, fromHistory);
}

function extractValueFromHtmlInput(elem: Element | HTMLInputElement | HTMLSpanElement) {
    let value = "";
    if (elem instanceof HTMLInputElement) {
        const inputElem = (elem as HTMLInputElement);
        if (inputElem) {
            if (inputElem.type === "radio" || inputElem.type === "checkbox") {
                if (inputElem.getAttribute("checked") === "") {
                    inputElem.checked = false;
                }
                value = inputElem.value === "on" ? (inputElem.checked ? "true" : "false") : inputElem.value;
            } else {
                value = inputElem.value;
            }
        }
    } else if (elem instanceof HTMLSpanElement) {
        const type = elem.getAttribute("data-type");
        value = type === undefined ? value : type;
    }
    return value;
}

const updateCartCallback = (resp: UpdateCartResponse) => {
    if (resp.url) {
        window.location.href = resp.url;
        return;
    }
    const productCount = $("[data-productcount]");
    if (productCount) {
        const count = productCount.getAttribute("data-productcount");
        $(".cart-icon-menu .count").innerHTML = count;
        // Duplicate for responsive
        $(".cart-r .count").innerHTML = count;
    }
    if (resp.cart) {
        updateShoppingCartHtml(resp.cart);
        if(resp.lastShoppingCartAction) {
            const [orderProductId, params] = resp.lastShoppingCartAction.split("_", 2)
            const candidates = $$("[data-orderproductid='" + orderProductId + "'] [data-action][data-params='" + params + "']")
            let tabbable = null;
            candidates.forEach(x => {
                const candidate = closestTabbable(x)
                if (candidate) {
                    tabbable = candidate
                    return
                }
            })
            tabbable?.focus();
        }
    }
    if (resp.cartMetaData) {
        $$("[data-products-translation]").forEach(x => x.innerHTML = resp.cartMetaData.productsTranslation);
        $$("[data-cart-products-total]").forEach(x => x.innerHTML = resp.cartMetaData.cartProductsTotal);
    }
    const cartIconMenu = $(".cart-icon-menu");
    cartIconMenu.classList.add("icon-menu--active");

    const countElement = cartIconMenu.querySelector(".count") as HTMLElement;
    countElement.classList.toggle("count--active", resp.count > 0);
    countElement.innerText = resp.count.toString();

    // Duplicate for responsive
    const cartResponsive = $(".cart-r");
    cartResponsive.classList.toggle("cart-r--active", resp.count > 0);
    const rCountElement = cartResponsive.querySelector(".count") as HTMLElement;
    rCountElement.innerText = resp.count.toString();
    gtmEvent(GtmEventType.AddToCart, null, null, null, null, resp.gtmAddToCartOptions)
    preserveUrlParameters()
};

function compileFormData(orderProductElem: Element, action: ShoppingCartAction, elem: Element) {
    const formData = new FormData();
    let value = extractValueFromHtmlInput(elem)
    formData.append("orderProductId", orderProductElem?.getAttribute("data-orderproductid") || null);
    formData.append("action", action.toString());
    formData.append("value", value || null);
    formData.append("step", CheckoutStep.products.toString());
    formData.append("additionalCampaignId", orderProductElem?.getAttribute("data-additionalcampaignid") || null);
    formData.append("warrantyPeriod", elem?.getAttribute("data-warranty-period") || null);
    formData.append("warrantyType", elem?.getAttribute("data-warranty-type") || null);
    if (elem) {
        const productListId = elem.getAttribute("data-product-list-id");
        const parentProductListId = elem.parentElement.getAttribute("data-product-list-id");
        formData.append("editCartFrom", productListId || parentProductListId)
    }
    return formData;
}

function toggleService(orderProductElem: Element, action: ShoppingCartAction, elem: Element) {
    const qtyElem = orderProductElem?.querySelector(`[data-params='${ShoppingCartAction.Quantity}']`) as HTMLInputElement;
    if (action === ShoppingCartAction.QuantityLess && (!qtyElem || parseInt(qtyElem?.value) <= 1)) {
        return;
    }
    const orderProductQty = parseInt(qtyElem?.value);
    if (qtyElem) {
        qtyElem.value = orderProductQty.toString();
    }
    startLoadingAnimation();
    const formData = compileFormData(orderProductElem, action, elem);
    postFormData(window.config.toggleServiceUrl, formData, updateCartCallback);
}

/**
 * Toodete samm. Päis ja ostukorvi eelvaade peegeldavad muudatusi.
 */
async function updateShoppingCart(orderProductElem: Element, action: ShoppingCartAction, elem: Element) {
    const qtyElem = orderProductElem?.querySelector(`[data-params='${ShoppingCartAction.Quantity}']`) as HTMLInputElement;
    if (action === ShoppingCartAction.QuantityLess && (!qtyElem || parseInt(qtyElem?.value) <= 1)) {
        return;
    }

    const orderProductQty = parseInt(qtyElem?.value);
    if (qtyElem) {
        qtyElem.value = orderProductQty.toString();
    }

    startLoadingAnimation();
    const isEndOfLifeProduct = elem?.getAttribute("data-is-eol") === "True";

    if (isEndOfLifeProduct && (action === ShoppingCartAction.QuantityMore || action === ShoppingCartAction.Quantity)) {
        const qty = action === ShoppingCartAction.QuantityMore ? (orderProductQty + 1).toString() : qtyElem.value;
        const productId = elem.getAttribute("data-product-id")

        const exceedsEolQty = await new Promise<boolean>((resolve) => {
            CheckForEol(productId, qty, (result) => resolve(result));
        });

        if (exceedsEolQty) {
            $(".eol-"+productId).classList.remove("d-none");
            return;
        }
    }

    const formData = compileFormData(orderProductElem, action, elem);

    if (action === ShoppingCartAction.UpdateWarrantySerials) {
        $$("[data-warrantyserial]").forEach((i: HTMLInputElement) => {
            const id = i.closest("[data-orderproductid]").getAttribute("data-orderproductid");
            if (!id) return;
            formData.append(`warrantySerials[${id}]`, i.value);
        });
    }

    if (elem) {
        const productListId = elem.getAttribute("data-product-list-id");
        const parentProductListId = elem.parentElement.getAttribute("data-product-list-id");
        formData.append("editCartFrom", productListId || parentProductListId);
    }

    postFormData(window.config.updateShoppingCartUrl, formData, updateCartCallback);
}

function CheckForEol(productId: string, qty: string, callback: (result: boolean) => void): void {
    const checkEolFormData = new FormData();

    checkEolFormData.append("quantity", qty);
    checkEolFormData.append("productId", productId);

    postFormData(window.config.outletProductMaxQuantityReachedUrl, checkEolFormData, (resp: boolean) => {
        callback(resp);
    });
}


export function addProtectionPackage(orderProductElem: Element, elem: Element) {
    startLoadingAnimation();
    const formData = new FormData();

    formData.append("orderProductId", orderProductElem.getAttribute("data-orderproductid"));
    formData.append("action", elem.getAttribute("data-params"));
    formData.append("value", elem.getAttribute("data-type"));
    formData.append("warrantyPeriod", elem.getAttribute("data-period"));
    formData.append("warrantyType", elem.getAttribute("data-type"));
    const productListId = elem.getAttribute("data-product-list-id");
    const parentProductListId = elem.parentElement.getAttribute("data-product-list-id");
    formData.append("editCartFrom" , productListId || parentProductListId);
    postFormData(window.config.updateShoppingCartSimpleUrl, formData, (x) => {
        gtmEvent(GtmEventType.AddToCart, null, null, null, null, x.gtmAddToCartOptions)
    });
}

function initForm() {
    const paymentButtons = $$(".payment-button");
    const contractSigningRadio = $(".bigbank-contract-radio");
    const paymentFooter = get("payment-footer");
    const paymentForm = get("checkout-payment") as HTMLFormElement;
    paymentButtons.forEach(x => x.addEventListener("click", () => {
        if(x.classList.contains("payment-button--active"))
            return;
        paymentButtons.forEach(z => z.classList.remove("payment-button--active"));
        x.classList.add("payment-button--active");
        if (x.classList.contains("lhvlease")) {
            if (get("lease-action-required")) {
                hide(paymentFooter);
            }
        }
        else if(x.classList.contains("on-receiving")){
            updatePaymentFooter(true);
            updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SetPreferredPaymentOnReceiving, (resp) => EuronicsResponsePromiseHandler(resp, (r) => setPreferredPaymentCallback(r)));
        }
        else if(x.classList.contains("money-transfer")){
            updatePaymentFooter(true);
            updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SetPreferredPaymentMoneyTransfer, (resp) => EuronicsResponsePromiseHandler(resp, (r) => setPreferredPaymentCallback(r)));
        }
        else {
            show(paymentFooter);
        }
        toggle(get("smart-rent-container"), x.classList.contains("smartrent"));
        get("choose-rental-period")?.classList.remove("alert-validation--active", "input-validation-error");
        contractSigningRadio?.classList.toggle("bigbank-contract-radio--active", x.classList.contains("bigbank"));
        if(!x.classList.contains("bigbank")){
            contractSigningRadio?.classList.remove("input-validation-error")
            get("contract-radio-block")?.classList.remove("input-validation-error");
        }
    }));
}

function updateShoppingCartCheckout(form: HTMLFormElement, action: ShoppingCartAction, callback?: Function) {
    const activePaymentButton = $(".payment-button--active");
    if (action === ShoppingCartAction.SubmitCreditcardPayment) {
        const callback = (html: string) => {
            location.href = html
        }
        const callbackLv = (html: string) => {
            const template = document.createElement("template");
            template.innerHTML = html;
            document.body.appendChild(template.content);
            const forms = document.getElementsByTagName("form");
            const form = forms.item(forms.length - 1);
            form.submit();
        }

        if (!validateForm(form)) return false;

        let optionalToken = ""
        const tokenVal = activePaymentButton?.parentElement.getAttribute("data-token");
        if (tokenVal) { 
            optionalToken = "?token=" + tokenVal
        }
        const creditCardFormUrl = window.config.getCreditCardFormUrl + optionalToken
        postDataText(creditCardFormUrl, {}, !window.config.everyPayEnabled && window.config.countryCode == "LV" ? callbackLv : callback);

        return true;
    }
    const bankLinkProvider = activePaymentButton?.getAttribute("data-banklink-provider");
    const everyPayBankLinkSource = activePaymentButton?.getAttribute("data-everypay-banklink-source")
    if (action === ShoppingCartAction.SubmitBanklinkPayment) {
        const bankLinkPayment = get("banklink-payment") as HTMLInputElement;
        if (bankLinkPayment?.checked) {
            if (!bankLinkProvider && !everyPayBankLinkSource) {
                get("choose-banklink-provider").classList.add("alert-validation--active", "input-validation-error");
                return; // early exit since no bank was selected
            } else {
                get("choose-banklink-provider").classList.remove("alert-validation--active", "input-validation-error");
            }
        }
    }


    const map = new Map<string, string>([
        ["action", action.toString()],
        ["bankLinkProvider", bankLinkProvider],
        ["EveryPayBankLinkSource", everyPayBankLinkSource]
    ]);
    const noValidationEvents = [
        ShoppingCartAction.SetPreferredPaymentOtherPayments,
        ShoppingCartAction.SetPreferredPaymentBankLink,
        ShoppingCartAction.SetPreferredPaymentMoneyTransfer,
        ShoppingCartAction.SetPreferredPaymentCreditCard,
        ShoppingCartAction.SetPreferredPaymentGiftCard,
        ShoppingCartAction.SetPreferredPaymentTelia,
        ShoppingCartAction.SetPreferredPaymentOnReceiving,
        ShoppingCartAction.ResetBigBankPayment,
    ];
    return postFormCustom(form, map, callback, noValidationEvents.some(x => x === action));
}

function updateShoppingCartGiftcard(cartAction: ShoppingCartAction, value: string, callback?: Function) {
    const map = new Map<string, string>([
        ["action", cartAction.toString()],
        ["value", value],
        ["GiftCardCode", (get("GiftcardCode") as HTMLInputElement)?.value],
        ["GiftCardCheck", (get("GiftcardCheck") as HTMLInputElement)?.value]
    ]);
    return postFormCustom(get("checkout-payment") as HTMLFormElement, map, callback, cartAction === ShoppingCartAction.RemoveGiftCard);
}

export function validateParcelTerminal(forceValid = false, allowEmpty = true) {
    const searchField = get("ParcelLocationName") as HTMLInputElement;
    const selectedParcel = $("input[type=radio][name='PostPackagesModel.ParcelLocation.Id']:checked");
    const isValid = forceValid || !!selectedParcel || allowEmpty && !searchField.value.length;
    const valMsg = $(`[data-valmsg-for='${searchField.name}']`);
    get("choose-post-package-action").classList.toggle("input-validation-error", !isValid);
    get("choose-post-package-action").classList.toggle("input-validation-valid", isValid);
    valMsg?.classList.toggle("field-validation-error", !isValid);
    valMsg?.classList.toggle("field-validation-valid", isValid);
    return isValid;
}

export function validateStore(forceValid = false) {
    const selectedStroreField = get("StoreName") as HTMLInputElement;
    const selectedStore = document.querySelector("input[type=radio][name=StoreId]:checked") as HTMLInputElement;
    const isValid = forceValid || !!selectedStore;
    const valMsg = $(`[data-valmsg-for='${selectedStroreField.name}']`);
    get("choose-store-action").classList.toggle("input-validation-error", !isValid);
    get("choose-store-action").classList.toggle("input-validation-valid", isValid);
    valMsg?.classList.toggle("field-validation-error", !isValid);
    valMsg?.classList.toggle("field-validation-valid", isValid);
    return isValid;
}

function validateAndContinue(form: HTMLFormElement, closeElem: string, openElem: string, action: ShoppingCartAction, callback?: Function, shiftFocus: boolean = false) {
    if (action === ShoppingCartAction.UpdateShipping) {

        const selectedShipping = $("input[name='ShippingMethodId']:checked");
        if (!selectedShipping) {
            $("[data-shipping-method-container]").classList.add("input-validation-error");
            return
        }

        const parcelLocationName = get("ParcelLocationName") as HTMLInputElement;
        const isPostPackage = $("#PostPackage:checked");
        if (parcelLocationName) {
            const valid = validateParcelTerminal(!isPostPackage, false);
            if (!valid) {
                return;
            }
        }
        const billingAddressInput = $("[name=BillingAddress]");
        if (billingAddressInput && isVisible(billingAddressInput) && billingAddressInput.classList.contains("input-validation-error")) {
            return;
        }
        const zipCodeWarningBlock = get("wrong-index-message");
        if (isVisible(zipCodeWarningBlock)) {
            return;
        }

        // No need to validate NovastarLT. There is only 1 shop available, that is automatically preselected
        if (window.config.site != Site.NovastarLT) {
            const shopRadio = get("Shop") as HTMLInputElement;
            if (shopRadio) {
                const storeValid = validateStore(!shopRadio.checked);
                if (!storeValid) {
                    return;
                }
            }
        }

        const deliveryContainer = get("delivery-info-container");
        if (deliveryContainer) {
            if (isVisible(deliveryContainer) && deliveryContainer.querySelector("input[type='radio']") && !deliveryContainer.querySelector("input[type='radio']:checked")) {
                deliveryContainer.classList.add("input-validation-error");
                return;
            } else {
                deliveryContainer.classList.remove("input-validation-error");
            }
        }
    }
    if (updateShoppingCartCheckout(form, action, callback)) {
        let closeElemHtml = get(closeElem);
        let openElemHtml = get(openElem);
        let activeElemHtml = document.activeElement as HTMLElement;
        closeElemHtml?.classList.add("locked");
        openElemHtml?.classList.add("accordion__item--active");
        if(closeElemHtml && closeElemHtml.contains(activeElemHtml) && shiftFocus){
            //ootame kuni järgmine klots on avatud enne fookuse liigutamist.
            if(closeElemHtml && closeElemHtml.contains(activeElemHtml) && shiftFocus) {
                waitForElement("#" + openElem + " .accordion__title").then((title: HTMLElement) => {
                    if (title) {
                        //ootame 1ms, et oleks võimalik focus üle viia
                        sleep(1).then(() => {
                            title.focus();
                        })
                    }
                });
            }
        }
    }
}

const setPreferredPaymentCallback = function(resp: SetPreferredPaymentMethodResp){
    initGtmEvents(resp.gtmEventWrapper);
    if(resp.overview){
        get("overview-container").innerHTML = resp.overview;
    }
    const otherPaymentTab = get("other-payment") as HTMLInputElement;
    const onReceivingSelected = otherPaymentTab?.checked && !!$(".on-receiving.payment-button--active");
    const moneyTransferSelected = otherPaymentTab?.checked && !!$(".money-transfer.payment-button--active");

    $(".on-receiving-description")?.classList.toggle("on-receiving-description--active", onReceivingSelected && !!resp.overview);
    // No description for credit card.
    $(".money-transfer-description")?.classList.toggle("money-transfer-description--active", moneyTransferSelected);
}
function openIncreditFormWithPost(dto: InCreditApiDto) {
    let winURL = dto.winUrl;
    let params = {
        'uid': dto.uid,
        'webapi_form_item_price': dto.itemPrice,
        'webapi_form_item_title': dto.itemTitle,
        'webapi_form_client_name': dto.clientName,
        'webapi_form_client_surname': dto.clientSurname,
        'webapi_form_client_phone': dto.phone,
        'webapi_form_client_email': dto.email,
        'webapi_form_promo': dto.promo,
        'webapi_form_custom_value': dto.customValue,
        'webapi_return_url': dto.returnUrl
    };
    let form = document.createElement("form");
    form.setAttribute("method", "post");
    form.setAttribute("action", winURL);
    for (let i in params) {
        if (params.hasOwnProperty(i)) {
            let input = document.createElement('input');
            input.type = 'hidden';
            input.name = i;
            input.value = params[i];
            form.appendChild(input);
        }
    }
    document.body.appendChild(form);
    form.target = '_SELF';
    form.submit();
    document.body.removeChild(form);
}

const InCreditCallback = (resp: Response) => {
    resp.json().then((resp: any) => {
        const dto: InCreditApiDto = resp.result;
        openIncreditFormWithPost(dto);
    });
}
function updatePaymentFooter(showFooter: boolean) {
    const contractSigningRadio = $(".bigbank-contract-radio");
    get("choose-payment-provider")?.classList.remove("alert-validation--active", "input-validation-error");
    get("choose-banklink-provider")?.classList.remove("alert-validation--active", "input-validation-error");
    get("choose-lease-provider")?.classList.remove("alert-validation--active", "input-validation-error");
    get("choose-rental-period")?.classList.remove("alert-validation--active", "input-validation-error");
    get("choose-payment-method")?.classList.remove("alert-validation--active", "input-validation-error");
    const leasePaymentTab = get("lease-payment") as HTMLInputElement;
    if (leasePaymentTab?.checked && $(".bigbank.payment-button--active")) {
        contractSigningRadio.classList.add("bigbank-contract-radio--active")
    } else {
        contractSigningRadio?.classList.remove("bigbank-contract-radio--active", "input-validation-error")
        get("contract-radio-block")?.classList.remove("input-validation-error");
    }
    const paymentFooter = get("payment-footer");
    const submitPayment = get("submit-payment");
    toggle(paymentFooter, showFooter)
    if (submitPayment) {
        submitPayment.classList.remove("button--disabled");
    }
}
function initPaymentTabs() {
    const banklinkPayment = get("banklink-payment");
    const otherPayments = get("other-payment");
    const creditcardPayment = get("creditcard-payment");
    const giftcardPayment = get("giftcard-payment");
    const leasePayment = get("lease-payment");
    const paymentForm = get("checkout-payment") as HTMLFormElement;
    const paymentFooter = get("payment-footer");
    const teliaInfo = get("telia-info");
    const lhvInfo = get("lhv-info");
    const inbankInfo = get("inbank-info");
    const submitPayment = get("submit-payment");
    
    banklinkPayment?.addEventListener("click", () => {
        updatePaymentFooter(true);
        updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SetPreferredPaymentBankLink, (resp) => EuronicsResponsePromiseHandler(resp, (r) => setPreferredPaymentCallback(r)));
    });
    otherPayments?.addEventListener("click", () => {
        updatePaymentFooter(true);
        if ($(".on-receiving.payment-button--active")) {
            updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SetPreferredPaymentOnReceiving, (resp) => EuronicsResponsePromiseHandler(resp, (r) => setPreferredPaymentCallback(r)));
        } else if ($(".money-transfer.payment-button--active")) {
            updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SetPreferredPaymentMoneyTransfer, (resp) => EuronicsResponsePromiseHandler(resp, (r) => setPreferredPaymentCallback(r)));
        } else {
            updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SetPreferredPaymentOtherPayments, (resp) => EuronicsResponsePromiseHandler(resp, (r) => initGtmEvents(r)));
        }
    });
    creditcardPayment?.addEventListener("click", () => {
        updatePaymentFooter(true);
        updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SetPreferredPaymentCreditCard, (resp) => EuronicsResponsePromiseHandler(resp, (r) => setPreferredPaymentCallback(r)));
    });
    
    leasePayment?.addEventListener("click",
        () => {
            updatePaymentFooter(true);
        });
    giftcardPayment?.addEventListener("click", () => {
        let showFooter = false;
        if (get("confirm-gift-card-purchase")) {
            showFooter = true;
        }
        updatePaymentFooter(showFooter);
        updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SetPreferredPaymentGiftCard, 
            (resp) => EuronicsResponsePromiseHandler(resp, (r) => initGtmEvents(r.gtmEventWrapper)));
    });

    $$("[data-lease-provider]").forEach(x => x.addEventListener("click", () => {
        const leaseProvider = parseInt(x.getAttribute("data-lease-provider"));
        hide(teliaInfo);
        hide(inbankInfo);
        hide(lhvInfo);
        if (leaseProvider === LeaseProvider.Telia) {
            show(teliaInfo);
            submitPayment.classList.remove("button--disabled");
        } else if (leaseProvider === LeaseProvider.InBank) {
            show(inbankInfo);
            submitPayment.classList.remove("button--disabled");
        } else if (leaseProvider === LeaseProvider.LHV) {
            show(lhvInfo);
            submitPayment.classList.remove("button--disabled");
        }
    }));

    if (get("lease-action-required")) {
        $$("[name='payments']").forEach(x => x.addEventListener("click", () => {
            $(".lhvlease")?.classList.remove("payment-button--active");
            hide(get("lhv-info"));
            paymentFooter.classList.remove("payment-footer__hidden");
        }));
    }
}

export function initParcelTerminals() {
    const parcelField = get("ParcelLocationName") as HTMLInputElement;
    const hidden = get("ParcelLocation_Id") as HTMLInputElement;
    const saveParcelCb = $("[data-saved-parcel]");
    const savedParcelVal = saveParcelCb?.getAttribute("data-saved-parcel");
    const saveParcelContainer = saveParcelCb?.parentElement;

    const setHiddenVal = (newVal: string) => {
        if (!hidden) return;

        hidden.value = newVal;
        hidden.dispatchEvent(new Event("change"));
        if (saveParcelCb) {
            toggle(saveParcelContainer, newVal !== "0" && newVal !== savedParcelVal)
        }
    }

    hidden?.addEventListener("change", () => validateParcelTerminal());
    const parcelModalItem = $("[data-target=parcel-modal]");

    parcelModalItem?.addDataBoundEventListener("click", () => {
            const parcelSearch = get("ParcelLocationName") as HTMLInputElement;
            parcelSearch.focus();
            parcelSearch.value = "";
            parcelSearch.dispatchEvent(new Event('input', {bubbles: true}));
        },
        "data-search-bound")

    parcelModalItem?.addDataBoundEventListener("keydown", (e: KeyboardEvent) => {
            clickOnEnterKey(e, parcelModalItem)
        }, "enter-keypress-bound")
}

function initShipping() {
    if (isVisible($("[data-edit-step='" + CheckoutStep.shipping + "']"))) {
        return;
    }
    const toggleAddressFields = (force: boolean) => {
        const billingFields = $$(".address__component input[id^='BillingAddress_']");
        billingFields.forEach(i => i.toggleAttribute("disabled", force));

        const cb = get("DifferentFromBilling") as HTMLInputElement;
        if (cb) {
            cb.toggleAttribute("disabled", force);
            if (cb.checked) {
                const shippingFields = $$(".address__component input[id^='ShippingAddress_'], input[id^='Receiver_']");
                shippingFields.forEach(i => i.toggleAttribute("disabled", force));
            }
        }
    }

    $$("input[name='ShippingMethodId']").forEach(i => i.addEventListener("click", (e) => {
        $("[data-shipping-method-container]").classList.remove("input-validation-error");
        const target = e.target as Element;
        //setShippingCountyToRiga();
        const isHomeShipping = target.getAttribute("data-home-shipping") === "True";
        const isServiceSelected = target.getAttribute("data-service-selected") === "True";
        const shippingNext = get("shipping-next") as HTMLButtonElement;
        if (isHomeShipping) {
            const noteContainer = get("note-container");
            toggle(noteContainer, target.id === ShippingMethod.Express && isServiceSelected)
            target.closest(".accordion__item").querySelector(".cart-transport__content")?.prepend($(".cart-address"));
            if (window.config.site == Site.ElesenLT){
                const shippingNotBilling = (get("DifferentFromBilling") as HTMLInputElement).checked;
                const addToInfo = get(shippingNotBilling ? "shipping-info" : "billing-info");
                const postcode = addToInfo.querySelector(`[${DataAttributes.PostCode}]`) as HTMLInputElement;
                checkTransportRestriction(postcode, null, null, null, true);
            }
        }
        else{
            shippingNext.disabled = false;
        }
        toggleAddressFields(!isHomeShipping);
        if (target.id === ShippingMethod.Shop) {
            get("choose-store-action")?.click();
        } else if (target.id === ShippingMethod.PostPackage) {
            if (get("selected-post-package").hasAttribute("data-not-chosen"))
                get("choose-post-package-action").click();
        }
    }));

    $("input[name='ShippingMethodId'][data-home-shipping='True']:checked")?.click();

    const addNote = get("addNote");
    addNote?.addEventListener("click", () => {
        show(get("noteSection"));
        hide(addNote);
    });

    $$(`[${DataAttributes.PostCode}]`).forEach(x => x.addEventListener("input", () => {
        checkTransportRestriction(x as HTMLInputElement);
    }));

    initParcelTerminals();
}

const actionUpdateCart = "UpdateShoppingCart";
const toggleServiceAction = "ToggleService";

function validateShippingRegion(restriction: number, e: MouseEvent, shippingRestrictionMessage: HTMLElement, validateShippingAndContinue: () => void) {
    // Kui lisatud teenus(ed), siis kontrollime, et tarneaadress oleks Riia tsoonis
    const restrictionOnServices = (restriction & TransportRestriction.Services) !== 0;
    // Kui express(Indoor) on valitud ja tegu on raske tootega, siis kontrollime, et tarneaadress oleks Riia tsoonis
    const restrictionOnHeavy = (restriction & TransportRestriction.Heavy) !== 0 && (get(ShippingMethod.Express) as HTMLInputElement)?.checked;

    if (restrictionOnServices || restrictionOnHeavy) {

        const shippingNotBilling = (get("DifferentFromBilling") as HTMLInputElement).checked;
        const addToInfo = get(shippingNotBilling ? "shipping-info" : "billing-info");
        const postcode = addToInfo.querySelector(`[${DataAttributes.PostCode}]`) as HTMLInputElement;

        const callback = (limitedShipping) => {

            if (limitedShipping) {

                if (restrictionOnServices) {
                    // Pakume muuta tarneandmed või jätkata maksmisele teenuseta
                    e.stopPropagation();
                    openModalById("limited-shipping-modal");
                } else {
                    // Kui raske tarne tuppa on keelatud, siis hoiatame ja valime transport-välisukseni
                    e.stopPropagation();
                    setTransportRestrictionStyles();
                    setShippingMethodStyles(true);
                    show(shippingRestrictionMessage);
                }
            } else {
                validateShippingAndContinue();
            }
        }

        checkTransportRestriction(postcode, null, callback, validateShippingAndContinue);
    } else {
        validateShippingAndContinue();
    }
}

function initCartActions() {
    $$("[data-orderproductid]").forEach(op => {
        op.querySelectorAll(`[data-action=${actionUpdateCart}]`).forEach(a => {
            const cartAction: ShoppingCartAction = parseInt(a.getAttribute("data-params"));
            const listenerType = cartAction === ShoppingCartAction.Quantity ? "change" : "click";
            
            a.addDataBoundEventListener(listenerType, () => {
                updateShoppingCart(op, cartAction, a);
            }, "update-shopping-cart-bound");
            
            let htmlElem = a as HTMLElement;
            if(listenerType === "click"){
                a.addDataBoundEventListener("keydown", (event: KeyboardEvent) => {
                    clickOnEnterKey(event, htmlElem);
                }, "enter-keypress-bound");
            }
            
            //Custom radio buttonite ja checkboxide puhul tekitame eventlisteneri labeli peale kuna input element ise on hidden
            //Siis on tab-imisel fookus nähtaval ja kliendi jaoks loogilisel kohal
            if(a.parentElement instanceof HTMLLabelElement){
                (a.parentElement as HTMLLabelElement).addDataBoundEventListener("keydown", (event: KeyboardEvent) => {
                    clickOnEnterKey(event, htmlElem);
                }, "enter-keypress-bound");
            } else {
                a.addDataBoundEventListener("keydown", (event: KeyboardEvent) => {
                    clickOnEnterKey(event, htmlElem);
                }, "enter-keypress-bound");
            }
        });

        // Teenuste init
        op.querySelectorAll(`[data-action=${toggleServiceAction}]`).forEach(a => {
            const cartAction: ShoppingCartAction = parseInt(a.getAttribute("data-params"));
            const listenerType = cartAction === ShoppingCartAction.Quantity ? "change" : "click";
            a.addDataBoundEventListener(listenerType, () => {
                toggleService(op, cartAction, a);
            }, "service-action-bound");

            //Custom radio buttonite ja checkboxide puhul tekitame eventlisteneri labeli peale kuna input element ise on hidden
            //Siis on tab-imisel fookus nähtaval ja kliendi jaoks loogilisel kohal
            let htmlElem = a as HTMLElement;
            if(a.parentElement instanceof HTMLLabelElement) {
                a.parentElement.addDataBoundEventListener("keydown", (event: KeyboardEvent) => {
                    clickOnEnterKey(event, htmlElem);
                }, "enter-keypress-bound");
            } else {
                a.addDataBoundEventListener("keydown", (event: KeyboardEvent) => {
                    clickOnEnterKey(event, htmlElem);
                }, "enter-keypress-bound");
            }
        });
    });
}

export function initCart(initialLoad: boolean, fromHistory: boolean) {
    // Toodete samm
    initCartActions();


    // Vautšer
    const toggleVoucher = get("toggle-voucher");
    const voucherCode = get("voucher-code");
    toggleVoucher?.addEventListener("click", 
        () => updateShoppingCart(null, ShoppingCartAction.ToggleVoucher, voucherCode));

    // In-ADS
    initAddressSearch();
    initManualAddressSearch();

    get("DifferentFromBilling")?.addEventListener("change", e => {
        const target = e.target as HTMLInputElement;
        if (target.checked) {
            $$(".shipping-address input.input-validation-error").forEach(x => {
                x.classList.remove("input-validation-error");
                x.nextElementSibling?.classList?.replace("field-validation-error", "field-validation-valid");
            });
        }

        const container = target.closest(".cart-transport__content").querySelector(".shipping-address");
        toggle(container as HTMLElement, target.checked);
        container.querySelectorAll("input[id^='ShippingAddress_'], input[id^='Receiver_']").forEach(i => i.toggleAttribute("disabled", !target.checked));
        const info = get(target.checked ? "shipping-info" : "billing-info");
        checkTransportRestriction(info.querySelector(`[${DataAttributes.PostCode}]`) as HTMLInputElement);
    });

    const callback = (response: Response) => {
        return response.text().then(text => {
            if (response.status === StatusCodes.IM_A_TEAPOT) {
                window.location.href = text;
            } else {
                updateShoppingCartHtml(text);
            }
        });
    }

    const contactInfoNext = get("contact-info-next");
    const contactForm = get("checkout-contact") as HTMLFormElement;
    const billToLegalPersonCheckbox = get("BillToLegalPersonCheckbox");
    const billToLegalPersonContainer = get("BillToLegalPersonContainer");
    const cannotConvertVatPayerMessage = get("CannotConvertVatPayerMessage");
    const vatRegistrationNumber = <HTMLInputElement>get("Contacts_VatRegistrationNumber");
    contactInfoNext?.addEventListener("click", () => validateAndContinue(contactForm, "contact-information", "delivery", ShoppingCartAction.UpdateBilling, callback));
    contactInfoNext?.addDataBoundEventListener("keydown", (e) => 
    {
        if(e.key === Key.Enter){
            e.preventDefault();
            validateAndContinue(contactForm, "contact-information", "delivery", ShoppingCartAction.UpdateBilling, callback, true)
        }
    }, "enter-keypress-bound");
    billToLegalPersonCheckbox?.addEventListener("change", (e) => toggleBillToLegalPerson(e));
    vatRegistrationNumber?.addEventListener("focus", e => toggleVatPrefix(e));
    vatRegistrationNumber?.addEventListener("blur", e => toggleVatPrefix(e));
    let contactsVatNonPayer = <HTMLInputElement>get("Contacts_VatNonPayer");
    contactsVatNonPayer?.addEventListener('click', toggleVatNrInput)

    const contactsCompanyName = <HTMLInputElement>get("Contacts_CompanyName");
    const contactsCompanyRegNumber = <HTMLInputElement>get("Contacts_CompanyRegNumber");
    const initialValueQualifiedName = "data-initial-value";
    if (contactsCompanyRegNumber) {
        const initialValue = contactsCompanyRegNumber?.value
        contactsCompanyRegNumber.setAttribute(initialValueQualifiedName, initialValue);
    }
    if (window.config.countryCode == Country.LT) {
        if (contactsVatNonPayer?.checked && (<HTMLInputElement>billToLegalPersonCheckbox).checked) {
            toggleVatNrInput()
        }
        // In EE user needs to register as a company and log in with it.
        contactsCompanyName?.addEventListener("focusout", () => autoCompleteVatNr(contactForm, null, null, ShoppingCartAction.UpdateBilling, callback));
        contactsCompanyRegNumber?.addEventListener("focusout", () => autoCompleteVatNr(contactForm, null, null, ShoppingCartAction.UpdateBilling, callback));
    }

    const shippingNext = get("shipping-next");
    const deliveryForm = get("checkout-delivery") as HTMLFormElement;
    shippingNext?.addEventListener("click", (e) => {
        const restriction = parseInt((get("transport-restriction") as HTMLInputElement).value);
        const shippingRestrictionMessage = get("shipping-restriction-message");

        if (window.config.site == Site.EuronicsLV || window.config.site == Site.ElesenLT) {
            validateShippingRegion(restriction, e, shippingRestrictionMessage, validateShippingAndContinue);
        } else if (window.config.site == Site.EuronicsEE) {
            const limitedShipping = shippingRestrictionMessage && !isHidden(shippingRestrictionMessage);
            const hasSelectedServices = (restriction & TransportRestriction.Services) !== 0;
            if (limitedShipping && hasSelectedServices) {
                e.stopPropagation();
                openModalById("limited-shipping-modal");
            } else {
                validateShippingAndContinue();
            }
        } else {    
            validateShippingAndContinue();
        }

    });
    const shippingNextConfirmed = get("shipping-next-confirmed");
    shippingNextConfirmed?.addEventListener("click", () => {
        closeModal()
        validateShippingAndContinue();
    });
    
    shippingNextConfirmed?.addDataBoundEventListener("keydown", (e: KeyboardEvent) =>
    {
        if(e.key === Key.Enter){
            e.preventDefault();
            closeModal();
            validateAndContinue(deliveryForm, "delivery", "payment", ShoppingCartAction.UpdateShipping, callback, true);
        }
    }, "enter-keypress-bound");
    shippingNext?.addDataBoundEventListener("keydown", (e: KeyboardEvent) =>
    {
        if(e.key === Key.Enter){
            e.preventDefault();
            closeModal();
            validateAndContinue(deliveryForm, "delivery", "payment", ShoppingCartAction.UpdateShipping, callback, true);
        }
    }, "enter-keypress-bound");

    function validateShippingAndContinue() {
        validateAndContinue(deliveryForm, "delivery", "payment", ShoppingCartAction.UpdateShipping, callback);
    }

    function toggleVatNrInput() {
        vatRegistrationNumber.disabled = !vatRegistrationNumber.disabled
        if (vatRegistrationNumber.disabled) {
            vatRegistrationNumber.value = "";
        }
    }

    /*
    * If legal person section is hidden the inputs are disabled. When opening the section we enable the inputs. */
    function toggleBillToLegalPerson(e) {
        toggle(billToLegalPersonContainer)
        document.querySelectorAll('#BillToLegalPersonContainer input').forEach((input) => {
            if (e.target.checked) {
                input.removeAttribute("disabled")
            } else {
                input.setAttribute("disabled", "")
            }
            toggle(cannotConvertVatPayerMessage)
        });
    }


    /*
    * Autocompletes Vat number if registration number was filled.
    * 
    * When user filled vat number before company name, then we listen if user filled the company name and then resubmit it. */
    function autoCompleteVatNr(form: HTMLFormElement, closeElem: string, openElem: string, action: ShoppingCartAction, callback?: Function) {
        const regNoInitialValue = contactsCompanyRegNumber.getAttribute(initialValueQualifiedName)
        if (regNoInitialValue != contactsCompanyRegNumber.value) {
            const autocompleteOnly = <HTMLInputElement>get("AutocompleteOnly");
            autocompleteOnly.setAttribute("checked", "")
            autocompleteOnly.removeAttribute("disabled")
            validateAndContinue(form, closeElem, openElem, action, callback)
        }
    }

    function toggleVatPrefix(e: FocusEvent) {
        const el = <HTMLInputElement>e.target;
        if (e.type == "focus") {
            const countryCode = el.getAttribute("data-country-code");
            if (el.value.length == 0)
                el.value = countryCode;
        } else if (e.type == "blur") {
            const prefixes = ["EE", "LV", "LT"];
            if (prefixes.includes(el.value)) el.value = "";
        }
    }
    
    const paymentForm = get("checkout-payment") as HTMLFormElement;

    const redirectCallback = (resp: Response) => {
        resp.text().then(text => {
            if (resp.status === StatusCodes.IM_A_TEAPOT) {
                window.location.href = text;
            } else {
                get("payment-failure").innerHTML = text;
            }
        });
    }

    const bankLinkCallback = (response: Response) => {
        return response.text().then(text => {
            if (response.status === StatusCodes.IM_A_TEAPOT) {
                window.location.href = text;
            } else if (response.status === StatusCodes.INTERNAL_SERVER_ERROR) {
                get("payment-failure").innerHTML = text;
            } else {
                document.body.innerHTML = text;
                ($("form") as HTMLFormElement).submit();
            }
        });
    }

    const leaseApplicationCallback = (resp: Response) => {
        return resp.text().then(text => {
            updateShoppingCartHtml(text);
            window.scrollTo(0, 0)
        });
    }
    const submitPayment = get("submit-payment");
    submitPayment?.addEventListener("click", () => {
        if (!$("[name=payments]:checked")) {
            get("choose-payment-provider").classList.add("alert-validation--active", "input-validation-error");
            return;
        }
        let action: ShoppingCartAction;
        if ((get("lease-payment") as HTMLInputElement)?.checked) {
            const activeLink = $(".payment-button--active[data-lease-provider]");
            if (!activeLink) {
                get("choose-lease-provider").classList.add("alert-validation--active", "input-validation-error");
                return;
            }
            get("choose-lease-provider").classList.remove("alert-validation--active", "input-validation-error");

            const tariffCode = activeLink.getAttribute("data-tariff-code");
            if (tariffCode) {
                postData(window.config.getInBankApplicationUrl, tariffCode, redirectCallback)
                return;
            }
            const leaseProvider = parseInt(activeLink.getAttribute("data-lease-provider"));
            switch (leaseProvider) {
                case LeaseProvider.LHV:
                    action = ShoppingCartAction.SubmitLHVPayment;
                    break;
                case LeaseProvider.Telia:
                    action = ShoppingCartAction.SubmitTeliaPayment;
                    break;
                case LeaseProvider.InCredit:
                    updateShoppingCartCheckout(paymentForm, ShoppingCartAction.InCreditApplication, InCreditCallback);
                    return;
                case LeaseProvider.InBank:
                    updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SubmitInBankPayment, redirectCallback);
                    return;
                case LeaseProvider.BigBank:
                    validateRadioList(get("contract-radio-block"))
                    if ($("#bigbank-smart-id-radio:checked")) {
                        updateShoppingCartCheckout(paymentForm, ShoppingCartAction.BigBankFlowApplication, leaseApplicationCallback);
                    } else if ($("#bigbank-paper-radio:checked")) {
                        updateShoppingCartCheckout(paymentForm, ShoppingCartAction.BigBankApplication, leaseApplicationCallback);
                    }
                    return;
                case LeaseProvider.SbLizingas:
                    updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SubmitSbLizingasPayment, bankLinkCallback);
                    return;
                case LeaseProvider.SbLizingasPerku3:
                    updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SubmitPerku3Payment, bankLinkCallback);
                    return;
                case LeaseProvider.SmartRent:
                    const rentalPeriod = $(".rental-period-button.button--active")?.getAttribute("data-rental-period");
                    if(rentalPeriod){
                        const rentalPeriodNum = parseInt(rentalPeriod);
                        postData(window.config.getSmartRentRedirectUrl, rentalPeriodNum, redirectCallback)
                        return;
                    }
                    get("choose-rental-period").classList.add("alert-validation--active", "input-validation-error");
                    return;
            }
        }
        const creditcardPayment = get("creditcard-payment") as HTMLInputElement
        if(creditcardPayment?.checked && $("#google-pay-button.payment-button--active")){
            const googlePayCallback = (resp: Response ) =>{
                return resp.json().then((gpResp: any) => {
                    window.handleGooglePayResponse(gpResp);
                });
            }
            action = ShoppingCartAction.SubmitGooglePayPayment;
            updateShoppingCartCheckout(paymentForm, action, googlePayCallback);
            return;
        }
        if(creditcardPayment?.checked && $("#apple-pay-button.payment-button--active")){
            GetDataJsonSync(window.config.startApplePayPaymentUrl, window.handleApplePayResponse);
            return;
        }
        action ??= creditcardPayment?.checked ? ShoppingCartAction.SubmitCreditcardPayment : ShoppingCartAction.SubmitBanklinkPayment;
        // OTHER PAYMENTS (Latvia)
        const otherPayments = get("other-payment") as HTMLInputElement
        if (otherPayments?.checked) {
            get("choose-payment-method").classList.remove("alert-validation--active", "input-validation-error");

            // MoneyTransfer
            if ($("#money-transfer-payment .payment-button--active")) {
                action = ShoppingCartAction.SubmitMoneyTransferPayment
            }
            // Payment On Delivery
            else if ($("#on-delivery-payment .payment-button--active")) {
                action = ShoppingCartAction.SubmitPaymentOnDelivery;
            }
            // Payment On Receiving
            else if ($("#on-receiving-payment .payment-button--active")) {
                action = ShoppingCartAction.SubmitPaymentOnReceiving;
            }

            if (action === ShoppingCartAction.SubmitMoneyTransferPayment || action === ShoppingCartAction.SubmitPaymentOnDelivery || action === ShoppingCartAction.SubmitPaymentOnReceiving) {
                $("main").classList.remove("checkout");
                updateShoppingCartCheckout(paymentForm, action, callback);
                return;
            } else {
                get("choose-payment-method").classList.add("alert-validation--active", "input-validation-error");
            }
        }

        if ((get("moneytransfer-payment") as HTMLInputElement)?.checked) {
            $("main").classList.remove("checkout");
            updateShoppingCartCheckout(paymentForm, ShoppingCartAction.SubmitMoneyTransferPayment, callback);
        } else {
            validateAndContinue(paymentForm, "", "", action, bankLinkCallback);
        }
    });
    
    const resendBigbankSigning = get("resend-bigbank-signing");
    const resetBigbankPayment = get("reset-bigbank-payment");

    const form = get("checkout-payment") as HTMLFormElement;

    const resetInbankPayment = get("reset-inbank-payment");
    resetInbankPayment?.addEventListener("click", () => updateShoppingCartCheckout(form, ShoppingCartAction.ResetInBankPayment, callback));


    resetBigbankPayment?.addEventListener("click", () => updateShoppingCartCheckout(form, ShoppingCartAction.ResetBigBankPayment, callback));
    resendBigbankSigning?.addEventListener("click", () => {
        const signingType = parseInt(resendBigbankSigning.getAttribute("data-application-type"));
        let action = null;

        if (signingType == ApplicationSigningType.Paper) {
            action = ShoppingCartAction.BigBankApplication;
        } else if (signingType == ApplicationSigningType.SmartId) {
            action = ShoppingCartAction.BigBankFlowApplication;
        }

        if (action) {
            updateShoppingCartCheckout(form, action, callback);
        }
    });
    
    const resendSbLizingasSigning = get("resend-sb-lzingas-signing");
    const resetSbLizingasPayment = get("reset-sb-lzingas-payment");

    resetSbLizingasPayment?.addEventListener("click", () => updateShoppingCartCheckout(form, ShoppingCartAction.ResetSbLizingasPayment, callback));


    resendSbLizingasSigning?.addEventListener("click", () => {
        const action = parseInt(resendSbLizingasSigning.getAttribute("data-sb-action"));
        updateShoppingCartCheckout(form, action, bankLinkCallback);
    })
    
    const resendInCreditSigning = get("resend-incredit-signing");
    const resetInCreditPayment = get("reset-incredit-payment");

    resetInCreditPayment?.addEventListener("click", () => updateShoppingCartCheckout(form, ShoppingCartAction.ResetInCreditPayment, callback));

    resendInCreditSigning?.addEventListener("click", () => {
        updateShoppingCartCheckout(form, ShoppingCartAction.InCreditApplication, InCreditCallback);
    })
    
    const confirmGiftCardPurchase = get("confirm-gift-card-purchase");
    confirmGiftCardPurchase?.addEventListener("click", () => {
        $("main").classList.remove("checkout");
        updateShoppingCartCheckout(form, ShoppingCartAction.ConfirmGiftCardPurchase, callback);
    });

// Vormistamise sammud
    $$("[data-action]").forEach((x: HTMLInputElement) => {
        const action = x.getAttribute("data-action");
        if (action === actionUpdateCart) {
            const cartAction: ShoppingCartAction = parseInt(x.getAttribute("data-params"));
            if (cartAction === ShoppingCartAction.AddGiftCard || cartAction === ShoppingCartAction.RemoveGiftCard) {
                x.addEventListener("click", () => {
                    updateShoppingCartGiftcard(cartAction, x.value, callback);
                });
            }
        }
    });

    const toggleInputLockedAttribute = (i: Element, force = false) => {
        const input = i as HTMLInputElement | HTMLTextAreaElement;
        let attribute: string = null;
        if (input.type === "checkbox") {
            attribute = "disabled";
        } else if (input.type === "text" || input.type === "textarea") {
            attribute = "readonly";
        }

        if (attribute) {
            input.toggleAttribute(attribute, force);
        }
    }

    $$("[data-edit-step]").forEach(x => x.addEventListener("click", (e) => {
        e.stopPropagation();
        const step = parseInt((e.target as HTMLElement).getAttribute("data-edit-step"));
        loadCartStep(step, false);
    }));

    $$("[data-edit-step]").forEach(x => x.addDataBoundEventListener("keydown", (e) => {
        clickOnEnterKey(e, x)
    }, "enter-keypress-bound"));

    $$(".locked .input-field-editable input, .locked  .textarea__container .textarea").forEach((input: HTMLInputElement) => toggleInputLockedAttribute(input, true));

    let currentStepUrl = (get("CurrentStepUrl") as HTMLInputElement)?.value;
    if (currentStepUrl && !fromHistory) {
        if (currentStepUrl.indexOf("?") === -1){
            currentStepUrl += location.search
        }
        const step = parseInt((get("cart-step") as HTMLInputElement).value);
        if (initialLoad) {
            history.replaceState({step: step}, '', currentStepUrl);
        } else {
            history.pushState({step: step}, '', currentStepUrl);
        }
    }

    const idCardReturnUrl = (get("IdCardUrl") as HTMLInputElement)?.value;
    if (idCardReturnUrl) {
        const idCardLogin = document.querySelector("a.id-login") as HTMLAnchorElement;
        if (idCardLogin) {
            idCardLogin.href = idCardReturnUrl;
        }
    }

    const checkoutButtons = $$(".button-checkout");
    checkoutButtons.forEach(button => button?.addDataBoundEventListener("click", () => {
        const warranties = $$("[data-accept-warranty-terms]");
        const warrantiesValid = !warranties.length || Array.from(warranties).map(validateElement).reduce((prev, curr) => prev && curr, true);

        if (!warrantiesValid) return;

        const additionalGroups: string[] = [];
        const validGroups: string[] = [];
        $$("[data-additional-for]").forEach(x => {
            const radio = x as HTMLInputElement;
            const group = x.getAttribute("data-additional-for");
            if (!additionalGroups.includes(group)) {
                additionalGroups.push(group);
            }
            if (!validGroups.includes(group) && radio.checked) {
                validGroups.push(group);
            }
        });
        if (additionalGroups.length === validGroups.length) {
            if (warranties.length) {
                //updateShoppingCart suunab edasi Billing-sammule
                updateShoppingCart(null, ShoppingCartAction.UpdateWarrantySerials, null);
            } else {
                window.location.href = button.getAttribute("data-url");
            }
        } else {
            additionalGroups.forEach(x => {
                if (!validGroups.includes(x)) {
                    $(`[data-additional-container-for="${x}"]`).classList.add("input-validation-error");
                }
            });
            $(".input-validation-error[data-additional-container-for]").scrollIntoView();
        }
    }));

    initStoreAndPostPackageSelections();

    const lvLeaseApplicationCb = (data) => {
        return data.json().then((response: UpdateCartResponse) => {
            if (response.url) {
                window.location.href = response.url;
                return;
            }
            if (response.cart) {
                updateShoppingCartHtml(response.cart);
            }
        });
    }
    
    const initBigBankApplication = () => {
        if (!fromHistory)
            history.pushState({leaseProvider: LeaseProvider.BigBank}, '', currentStepUrl);
        const political = get("isPolitical") as HTMLInputElement;
        const politicalCommentContainer = get("political-comment-container") as HTMLElement;
        political.addDataBoundEventListener("click", () => toggle(politicalCommentContainer));
        initBigBankLeaseCalculator();
        const submit = get("submitBigBankApplication") as HTMLInputElement;
        submit.addDataBoundEventListener("click", () => {
            const form = get("bigbank-form") as HTMLFormElement;
            const map = new Map<string, string>();
            postFormCustom(form, map, lvLeaseApplicationCb);
        });
    }
    const initBigBankFlowApplication = () => {
        if (!fromHistory)
            history.pushState({leaseProvider: LeaseProvider.BigBankHostedFlow}, '', currentStepUrl);
        const submit = get("submitBigBankFlowApplication") as HTMLInputElement;
        submit.addDataBoundEventListener("click", () => {
            const form = get("bigbank-flow-form") as HTMLFormElement;
            const map = new Map<string, string>();
            postFormCustom(form, map, lvLeaseApplicationCb);
        });
    }
    initPaymentTabs();
    initForm();
    initLoggedOutButtons();
    initToggleButtons();
    initLogin();
    setTransportRestrictionStyles();
    initShipping();
    initCarousels();
    initPhoneInputs();
    initDatePickers();
    initInputListeners();
    adjustContentHeight();
    initRentalCalculator();
    initPasswordChecks();

    if (get("bigbank-form")) {
        initBigBankApplication();
    }
    if (get("bigbank-flow-form")) {
        initBigBankFlowApplication();
    }
    initDeleteCardTokenButtons();
    initConfirmDeleteToken();
    initLoadWarrantyOptions();
    if(typeof window.initApplePay === "function" ){
        window.initApplePay();
    }
}

export function adjustContentHeight() {
    const overview = $('.overview__content');
    const msgContainer = $('#message-container');
    const lrPanelVisible = $('.lr-panel--visible');
    if (overview) {
        if (msgContainer.clientHeight > 0) {
            overview.style.top = overview.getBoundingClientRect().top + msgContainer.clientHeight + 'px';
        } else {
            overview.style.top = null;
        }
    }
    if(lrPanelVisible){
        if (msgContainer.clientHeight > 0) {
            lrPanelVisible.style.top = lrPanelVisible.getBoundingClientRect().top - msgContainer.clientHeight + 'px';
        } else {
            lrPanelVisible.style.top = null;
        }
    }
}

function addedToCartInternal(button: HTMLElement, addToCartResp: AddToCartResponse) {
    if (button.hasAttribute("data-target")) {
        const target = button.getAttribute("data-target");
        closeModal();
        get("added-cart-panel-content").innerHTML = addToCartResp.addedCartPanel;
        openPanel(target)
    }
    if (addToCartResp.cartPanel) {
        get("cart-panel-wrapper").innerHTML = addToCartResp.cartPanel;
        updateHeaderCartHtml();
    }
}

export function addedToCart(resp: Response, button: HTMLElement) {
    if (resp.status === StatusCodes.OK) {
        return resp.json().then((y: AddToCartResponse) => {
            handleAddToCartResponse(button, y);
            const productId = button.getAttribute("data-add-to-cart-id");
            highlightProductCard(productId, true, "is-in-cart");
            preserveUrlParameters()
        });
    }
}

export function handleAddToCartResponse(button: HTMLElement, addToCartResp: AddToCartResponse) {

    if (addToCartResp.giftCardWithOtherProductsModal) {
        get("cart-modal-container").innerHTML = addToCartResp.giftCardWithOtherProductsModal;
        initModals();
        const buttonId = button.getAttribute("id");
        openModalById("cart-modal", buttonId);
    } else {
        addedToCartInternal(button, addToCartResp);
    }


    if (addToCartResp.cart) {
        updateShoppingCartHtml(addToCartResp.cart);
    }
    if (addToCartResp.gtmAddToCartOptions) {
        gtmEvent(GtmEventType.AddToCart, "", null, null, null, addToCartResp.gtmAddToCartOptions)
    }
    if(addToCartResp.klaviyoAddedToCart && window.klaviyo !== undefined){
        window.klaviyo.push(['track', KlaviyoEvents.AddedToCart, addToCartResp.klaviyoAddedToCart])
    }

    if (addToCartResp.cartMetaData) {
        $$("[data-products-translation]").forEach(x => x.innerHTML = addToCartResp.cartMetaData.productsTranslation);
        $$("[data-cart-products-total]").forEach(x => x.innerHTML = addToCartResp.cartMetaData.cartProductsTotal);
    }
}

function initLoggedOutButtons() {
    $$("[data-logged-out-step]").forEach(x => x.addDataBoundEventListener("click", () => {
        const step = parseInt(x.getAttribute("data-logged-out-step"));
        const corpButton = get("register-corporate");
        const isCorporate = (corpButton ?? false) && corpButton.classList.contains("button--tertiary");
        const data = {step: step, isCorporate: isCorporate};
        postDataText(window.config.updateLoggedOutCartStepUrl, data, updateShoppingCartHtml);
    }));
}

/**
 * Juhib hoiatusklasside näitamist transpordielementidel.
 * Eeltingimus: ostukorvis on vähemalt 1 VEDU-TUPPA või VEDU-RASKE toode või paigaldus- või äraveoteenus
 * Klass lisatakse elemendile, kui on täidetud 3 tingimust, vastasel juhul klass eemaldatakse:
 *  1) Element viitab, et aadress asub saarel (data-no-transport atribuut)
 *  2) Element on osa tarneaadressist (shippinginfo vs billinginfo)
 *  3) Element on osa nähtavast aadressivalikust (otsing vs käsitsi sisestamine)
 * Hoiatusteadet näitame, kui leidub vähemalt 1 hoiatusklassiga element
 * */
export function setTransportRestrictionStyles() {
    const shippingRestrictionMessage = get("shipping-restriction-message");
    if (!shippingRestrictionMessage){
        return;
    }
    const noTransport = "no-transport";
    const shippingNotBilling = (get("DifferentFromBilling") as HTMLInputElement).checked;
    const addToInfo = get(shippingNotBilling ? "shipping-info" : "billing-info");
    const removeFromInfo = get(shippingNotBilling ? "billing-info" : "shipping-info");
    removeFromInfo.querySelectorAll(`.${noTransport}`).forEach(x => x.classList.remove(noTransport));

    $$(`.${noTransport}`).forEach(x => {
        if (!x.hasAttribute(DataAttributes.NoTransport)) {
            x.classList.remove(noTransport);
        }
    });
    let showMessage = false;

    if (addToInfo.querySelector(".manual-address-container").classList.contains("manual-address__hidden")) {
        addClassIfAttr(addToInfo.querySelector(".address-search-component .address-field"));
    } else {
        addClassIfAttr(addToInfo.querySelector(".dropdown-county"));
        addClassIfAttr(addToInfo.querySelector(`[${DataAttributes.PostCode}]`));
    }

    function addClassIfAttr(elem: Element) {
        if (elem?.hasAttribute(DataAttributes.NoTransport)) {
            elem.classList.add(noTransport);
            showMessage = true;
        }
    }

    setShippingMethodStyles(showMessage);
}

function setShippingMethodStyles(disableExpress: boolean) {
    const restriction = get("transport-restriction") as HTMLInputElement;
    const restrictionVal = parseInt(restriction.value);
    const heavy = (restrictionVal & TransportRestriction.Heavy) !== 0;
    const indoor = (restrictionVal & TransportRestriction.Indoor) !== 0;
    const shippingRestrictionMessage = get("shipping-restriction-message");
    toggle(shippingRestrictionMessage, disableExpress && (heavy  || indoor))
    if (!heavy && !indoor) {
        return;
    }
    const expressDelivery = get(ShippingMethod.Express) as HTMLInputElement;
    const homeDelivery = get(ShippingMethod.HomeDelivery) as HTMLInputElement;
    const expressContainer = $(`[data-container-for="${ShippingMethod.Express}"]`);
    const homeContainer = $(`[data-container-for="${ShippingMethod.HomeDelivery}"]`);
    if (heavy) {
        const expressLabel = expressContainer.querySelector(".radio__label");
        if (disableExpress) {
            if (window.config.site == Site.EuronicsEE) {
                expressLabel.textContent = restriction.getAttribute("data-home-description");
            } else if (window.config.site == Site.EuronicsLV || window.config.site == Site.ElesenLT) {
                if (expressDelivery.checked) {
                    homeDelivery.click();
                    show(homeContainer);
                    show(homeContainer.parentElement)
                }
                expressContainer.classList.add("shipping-method-container--disabled");
            }
        } else {
            if (window.config.site == Site.EuronicsEE) {
                expressLabel.textContent = restriction.getAttribute("data-express-description");
            } else if (window.config.site == Site.EuronicsLV || window.config.site == Site.ElesenLT) {
                expressContainer.classList.remove("shipping-method-container--disabled");
            }
        }
    } else {
        if (disableExpress) {
            if (expressDelivery.checked) {
                homeDelivery.click();
                show(homeContainer);
                show(homeContainer.parentElement)
            }
            expressContainer.classList.add("shipping-method-container--disabled");
        } else {
            expressContainer.classList.remove("shipping-method-container--disabled");
            if ((restrictionVal & TransportRestriction.Services) !== 0) {
                hide(homeContainer);
                expressDelivery.click();
                expressDelivery.closest(".accordion__item")?.classList.add("accordion__item--active");
            }
        }
    }
}


export function checkTransportRestriction(postCode: HTMLInputElement, addressField: Element = null, callbackOuter?: Function, validateShippingAndContinue?: () => void, checkOnlyNidaRestriction : boolean = false) {

    const postCodeLength = window.config.countryCode == Country.EE || window.config.site == Site.ElesenLT
        ? 5
        : window.config.countryCode == Country.LV
            ? 4
            : 0;

    if (postCode.value.length === postCodeLength) {

        const callback = (resp: IsTransportRestrictionResponse) => {

            let limitedShipping = false;
            const elesenShippingRestrictionMessages = $$(".alert--elesen-shipping");
            const shippingNext = get("shipping-next") as HTMLButtonElement;
            const nidaRestrictionMsg = get("nida-shipping-restriction-message") as HTMLElement;
            
            if(resp.isNidaTransportRestriction){
                shippingNext.disabled = true;
                show(nidaRestrictionMsg);
            }
            else{
                shippingNext.disabled = false;
                hide(nidaRestrictionMsg);
            }
            
            if(checkOnlyNidaRestriction){
                return;
            }

            if (resp.isTransportRestriction || resp.isNidaTransportRestriction) {
                postCode.setAttribute(DataAttributes.NoTransport, "");
                if (addressField) {
                    addressField.setAttribute(DataAttributes.NoTransport, "");
                }
                limitedShipping = true;
                elesenShippingRestrictionMessages.forEach(x => x.classList.remove("alert--elesen-shipping--faded"));
            } else {
                postCode.removeAttribute(DataAttributes.NoTransport);
                if (addressField) {
                    addressField.removeAttribute(DataAttributes.NoTransport);
                }
                elesenShippingRestrictionMessages.forEach(x => x.classList.add("alert--elesen-shipping--faded"));
            }

            setTransportRestrictionStyles();
            if (callbackOuter) {
                callbackOuter(limitedShipping);
            }
        }
        postDataJson(window.config.isTransportRestrictionUrl, postCode.value, callback);
    } else {
        postCode.removeAttribute(DataAttributes.NoTransport);
        if (addressField) {
            addressField.removeAttribute(DataAttributes.NoTransport);
        }
        setTransportRestrictionStyles();
        // validateShippingAndContinue pidi toimuma callback-i sees, aga early-exit tähendab, et seda ei kutsuta välja.
        if (callbackOuter) {
            validateShippingAndContinue();
        }
    }
}

export function loadCartStep(step: number, fromHistory: boolean) {
    const callback = (data: string) => {
        updateShoppingCartHtml(data, fromHistory);
    }
    postDataText(window.config.editCartStepUrl, step, callback);
}

export function loadLeaseForm(leaseProvider: LeaseProvider) {
    const callback = (data: string) => {
        updateShoppingCartHtml(data, true);
    }
    postDataText(window.config.loadLeaseFormUrl, leaseProvider, callback);
}

function initDeleteCardTokenButtons(){
    $$("[data-delete-token]").forEach(x => {
        x.addDataBoundEventListener("click", (e) =>{
            get("delete-card-token-confirmed").setAttribute("data-delete-token", x.getAttribute("data-delete-token"));
            e.stopPropagation();
            openModalById("delete-card-token-modal");
        });
    });
}
function initConfirmDeleteToken(){
    const confirmBtn = get("delete-card-token-confirmed");
    if(confirmBtn){
        confirmBtn.addDataBoundEventListener("click", () => {
            const cardToken = confirmBtn.getAttribute("data-delete-token");
                postData(window.config.deleteCardTokenUrl, cardToken);
               $(`[data-token="${cardToken}"]`).remove(); 
               closeModal();
        });
    }
}

function initLoadWarrantyOptions(){
    $$("[data-load-warranty-order-product-id]").forEach(x => {
        x.addDataBoundEventListener("click", () =>{
            const accordionContent = x.parentElement.querySelector(".accordion__content")
            if(!accordionContent.innerHTML.trim()){
                const data = {orderProductId: parseInt(x.getAttribute("data-load-warranty-order-product-id")), isInsurance: x.getAttribute("data-load-warranty-is-insurance") === "True" };
                const callback = (data: string) => {
                    accordionContent.innerHTML = data;
                    initCartActions()
                }
                postDataText(window.config.loadWarrantyOptionsUrl, data, callback)
            }
        }, "loadWarrantyOptions")
    })
}